// Advanced filter modal for the property list page.
//
// Two stacked sections:
//
//  1. AI input ("Tìm bằng câu hỏi") — user types a Vietnamese
//     question, we POST /ai/extract-filters, and the response is
//     held in dialog-local state. A "Áp dụng vào bộ lọc" button
//     copies the extracted values into the manual filter fields
//     below so the user can review or tweak before committing.
//
//  2. Manual fields — listing type / status / ward / bedrooms /
//     bathrooms / area range / price range.
//
// Footer commits the current draft to the URL. The dialog does NOT
// write to the URL until the user clicks "Áp dụng" — opening, fiddling
// with fields, and cancelling all leave the URL untouched.

import { useState, type ChangeEvent, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { localizeError } from "@/lib/errorMessages";
import {
  useExtractFilters,
  type AiExtractedFilters,
} from "@/features/ai/api";
import { WARD_OPTIONS } from "@/features/property/utils/ward-options";
import { WardCombobox } from "@/components/ward-combobox";
import {
  readFilterDraft,
  writeFilterDraft,
  type PropertyFilterDraft,
} from "@/features/property/utils/property-filter-params";
import type { PropertyStatus } from "@/types/property";

const ANY_VALUE = "__ANY__";

const STATUS_OPTIONS: Array<{ value: PropertyStatus; label: string }> = [
  { value: "AVAILABLE", label: "Đang trống" },
  { value: "DEPOSIT", label: "Đặt cọc" },
  { value: "SOLD", label: "Đã bán" },
  { value: "RENTED", label: "Đã thuê" },
];

interface AdvancedFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdvancedFilterDialog({
  open,
  onOpenChange,
}: AdvancedFilterDialogProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Dialog-local working copy. Initialized from the URL each time the
  // dialog opens, and discarded on cancel.
  const [draft, setDraft] = useState<PropertyFilterDraft>(() =>
    readFilterDraft(searchParams),
  );
  const [aiPrompt, setAiPrompt] = useState("");
  const [extracted, setExtracted] = useState<AiExtractedFilters | null>(null);
  const [aiTouched, setAiTouched] = useState(false);
  const extract = useExtractFilters();

  // Reset the working state every time the dialog transitions from
  // closed→open. Done via the "adjust state during render" pattern
  // (tracking the previous `open` value) so we don't need a useEffect
  // + setState combo — the React Compiler rule blocks the latter.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraft(readFilterDraft(searchParams));
      setExtracted(null);
      setAiTouched(false);
    }
  }

  const handleExtract = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const message = aiPrompt.trim();
    if (message.length === 0) return;
    extract.mutate(message, {
      onSuccess: (data) => {
        const filters = data.filters ?? {};
        setExtracted(filters);
        setAiTouched(true);
      },
      onError: (error: unknown) => {
        toast.error(localizeError(error));
      },
    });
  };

  const applyExtractedToDraft = () => {
    if (extracted === null) return;
    setDraft((prev) => mergeExtractedIntoDraft(prev, extracted));
    setExtracted(null);
    setAiTouched(true);
  };

  const handleApply = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        writeFilterDraft(next, draft, { markAi: aiTouched });
        return next;
      },
      { replace: true },
    );
    onOpenChange(false);
  };

  const handleReset = () => {
    setDraft({});
    setExtracted(null);
    setAiTouched(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] gap-0 overflow-y-auto p-0 sm:max-w-2xl">
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle>Bộ lọc nâng cao</DialogTitle>
          <DialogDescription>
            Tinh chỉnh từng trường thủ công hoặc dùng AI để tự động điền.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 px-5 py-5">
          <AiPromptSection
            prompt={aiPrompt}
            onPromptChange={setAiPrompt}
            isPending={extract.isPending}
            extracted={extracted}
            onSubmit={handleExtract}
            onApplyExtracted={applyExtractedToDraft}
          />

          <ManualFiltersSection draft={draft} onChange={setDraft} />
        </div>

        <DialogFooter className="border-t border-border bg-muted/30 px-5 py-3 sm:justify-between">
          <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
            Đặt lại
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleApply}
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              Áp dụng
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AI section
// ---------------------------------------------------------------------------

function AiPromptSection({
  prompt,
  onPromptChange,
  isPending,
  extracted,
  onSubmit,
  onApplyExtracted,
}: {
  prompt: string;
  onPromptChange: (next: string) => void;
  isPending: boolean;
  extracted: AiExtractedFilters | null;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onApplyExtracted: () => void;
}) {
  return (
    <section
      aria-label="Tìm bằng AI"
      className="rounded-lg border border-primary/20 bg-primary/[0.04] p-4"
    >
      <header className="mb-2 flex items-center gap-1.5">
        <Sparkles aria-hidden="true" className="size-4 text-primary" />
        <h3 className="text-sm font-semibold">Tìm bằng câu hỏi</h3>
      </header>
      <p className="mb-3 text-xs text-muted-foreground">
        Mô tả ngắn gọn — AI sẽ trích các trường lọc bên dưới.
      </p>
      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <Textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="VD: căn 2 phòng ngủ dưới 3 tỷ tại Hoàn Kiếm"
          rows={2}
          className="resize-none"
          disabled={isPending}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={isPending || prompt.trim().length === 0}
          >
            <Sparkles aria-hidden="true" className={isPending ? "animate-pulse" : ""} />
            {isPending ? "Đang phân tích..." : "Phân tích"}
          </Button>
        </div>
      </form>

      {extracted !== null ? (
        <div className="mt-3 flex flex-col gap-2 rounded-md border border-border bg-card p-3">
          <p className="text-[12px] font-medium text-muted-foreground">
            AI đã trích được:
          </p>
          <ExtractedPreview filters={extracted} />
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="secondary" onClick={onApplyExtracted}>
              Áp dụng vào bộ lọc
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ExtractedPreview({ filters }: { filters: AiExtractedFilters }) {
  const labels = describeExtracted(filters);
  if (labels.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        AI không nhận diện được trường lọc nào. Thử mô tả cụ thể hơn.
      </p>
    );
  }
  return (
    <ul className="flex flex-wrap gap-1.5">
      {labels.map((label) => (
        <li
          key={label}
          className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs text-primary"
        >
          {label}
        </li>
      ))}
    </ul>
  );
}

function describeExtracted(filters: AiExtractedFilters): string[] {
  const out: string[] = [];
  if (filters.listingType) {
    out.push(filters.listingType === "SALE" ? "Bán" : "Cho thuê");
  }
  if (filters.status) out.push(filters.status);
  if (filters.wardCode) {
    const found = WARD_OPTIONS.find((w) => w.value === filters.wardCode);
    out.push(found?.label ?? filters.wardCode);
  }
  if (filters.bedrooms !== undefined && filters.bedrooms !== null) {
    out.push(`${filters.bedrooms} PN`);
  }
  if (filters.bathrooms !== undefined && filters.bathrooms !== null) {
    out.push(`${filters.bathrooms} WC`);
  }
  if (filters.areaMin !== undefined && filters.areaMin !== null) {
    out.push(`≥ ${filters.areaMin} m²`);
  }
  if (filters.areaMax !== undefined && filters.areaMax !== null) {
    out.push(`≤ ${filters.areaMax} m²`);
  }
  if (filters.priceMin !== undefined && filters.priceMin !== null) {
    out.push(`từ ${filters.priceMin.toLocaleString("vi-VN")}`);
  }
  if (filters.priceMax !== undefined && filters.priceMax !== null) {
    out.push(`đến ${filters.priceMax.toLocaleString("vi-VN")}`);
  }
  return out;
}

function mergeExtractedIntoDraft(
  base: PropertyFilterDraft,
  extracted: AiExtractedFilters,
): PropertyFilterDraft {
  const merged: PropertyFilterDraft = { ...base };
  if (extracted.listingType !== undefined && extracted.listingType !== null) {
    merged.listingType = extracted.listingType;
  }
  if (extracted.status !== undefined && extracted.status !== null) {
    merged.status = extracted.status;
  }
  if (extracted.wardCode !== undefined && extracted.wardCode !== null) {
    merged.wardCode = extracted.wardCode;
  }
  if (extracted.bedrooms !== undefined && extracted.bedrooms !== null) {
    merged.bedrooms = extracted.bedrooms;
  }
  if (extracted.bathrooms !== undefined && extracted.bathrooms !== null) {
    merged.bathrooms = extracted.bathrooms;
  }
  if (extracted.areaMin !== undefined && extracted.areaMin !== null) {
    merged.areaMin = extracted.areaMin;
  }
  if (extracted.areaMax !== undefined && extracted.areaMax !== null) {
    merged.areaMax = extracted.areaMax;
  }
  if (extracted.priceMin !== undefined && extracted.priceMin !== null) {
    merged.priceMin = extracted.priceMin;
  }
  if (extracted.priceMax !== undefined && extracted.priceMax !== null) {
    merged.priceMax = extracted.priceMax;
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Manual filters section
// ---------------------------------------------------------------------------

function ManualFiltersSection({
  draft,
  onChange,
}: {
  draft: PropertyFilterDraft;
  onChange: (next: PropertyFilterDraft) => void;
}) {
  const set = <K extends keyof PropertyFilterDraft>(
    key: K,
    value: PropertyFilterDraft[K] | undefined,
  ) => {
    onChange({ ...draft, [key]: value });
  };

  const numberFromEvent = (
    event: ChangeEvent<HTMLInputElement>,
  ): number | undefined => {
    const raw = event.target.value;
    if (raw === "") return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  return (
    <section aria-label="Bộ lọc thủ công" className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldGroup label="Trạng thái">
          <Select
            value={draft.status ?? ANY_VALUE}
            onValueChange={(next) =>
              set(
                "status",
                next === ANY_VALUE ? undefined : (next as PropertyStatus),
              )
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Tất cả" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_VALUE}>Tất cả</SelectItem>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldGroup>

        <FieldGroup label="Khu vực">
          <WardCombobox
            value={draft.wardCode ?? null}
            onChange={(next) => set("wardCode", next ?? undefined)}
            clearable
            clearLabel="Tất cả"
            placeholder="Tất cả"
          />
        </FieldGroup>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <FieldGroup label="Phòng ngủ">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={draft.bedrooms ?? ""}
            onChange={(e) => set("bedrooms", numberFromEvent(e))}
            placeholder="—"
          />
        </FieldGroup>
        <FieldGroup label="Phòng tắm">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={draft.bathrooms ?? ""}
            onChange={(e) => set("bathrooms", numberFromEvent(e))}
            placeholder="—"
          />
        </FieldGroup>
        <FieldGroup label="Diện tích từ (m²)">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={draft.areaMin ?? ""}
            onChange={(e) => set("areaMin", numberFromEvent(e))}
            placeholder="—"
          />
        </FieldGroup>
        <FieldGroup label="Diện tích đến (m²)">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={draft.areaMax ?? ""}
            onChange={(e) => set("areaMax", numberFromEvent(e))}
            placeholder="—"
          />
        </FieldGroup>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FieldGroup label="Giá từ (VND)">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1_000_000}
            value={draft.priceMin ?? ""}
            onChange={(e) => set("priceMin", numberFromEvent(e))}
            placeholder="—"
          />
        </FieldGroup>
        <FieldGroup label="Giá đến (VND)">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1_000_000}
            value={draft.priceMax ?? ""}
            onChange={(e) => set("priceMax", numberFromEvent(e))}
            placeholder="—"
          />
        </FieldGroup>
      </div>
    </section>
  );
}

function FieldGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-wider text-ink-faint">
        {label}
      </Label>
      {children}
    </div>
  );
}
